import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Wallet, MagnifyingGlass, Check, X as XIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import PaymentMethodTypesManager from './PaymentMethodTypesManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SellerDirectPaymentsView = () => {
  const [settings, setSettings] = useState({ feature_enabled: true, allowed_types: [] });
  const [types, setTypes] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  const fetchAll = useCallback(async () => {
    try {
      const [s, t, list] = await Promise.all([
        axios.get(`${API_URL}/api/admin/direct-payments/settings`),
        axios.get(`${API_URL}/api/admin/direct-payments/method-types`),
        axios.get(`${API_URL}/api/admin/direct-payments/sellers`),
      ]);
      setSettings(s.data || {});
      setTypes(t.data?.types || []);
      setSellers(list.data?.sellers || []);
    } catch (e) { toast.error('Failed to load'); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleFeature = async (on) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/direct-payments/settings`, { feature_enabled: on });
      setSettings(res.data);
      toast.success(on ? 'Feature enabled' : 'Feature disabled');
    } catch (e) { toast.error('Update failed'); }
  };
  const toggleType = async (typeId) => {
    const next = settings.allowed_types.includes(typeId)
      ? settings.allowed_types.filter(x => x !== typeId)
      : [...settings.allowed_types, typeId];
    try {
      const res = await axios.put(`${API_URL}/api/admin/direct-payments/settings`, { allowed_types: next });
      setSettings(res.data);
    } catch (e) { toast.error('Update failed'); }
  };

  const refreshSellers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/direct-payments/sellers`, { params: { search: search || undefined } });
      setSellers(res.data?.sellers || []);
    } catch (e) { /* ignore */ }
  };

  const toggleSelect = (uid) => {
    const next = new Set(selected);
    next.has(uid) ? next.delete(uid) : next.add(uid);
    setSelected(next);
  };
  const bulkSet = async (action, target) => {
    try {
      const body = { action, target };
      if (target === 'selected') body.user_ids = Array.from(selected);
      const res = await axios.post(`${API_URL}/api/admin/direct-payments/bulk-set`, body);
      toast.success(`${action}d ${res.data?.modified || 0} sellers`);
      setSelected(new Set());
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed'); }
  };

  const filtered = sellers; // backend already filters; this is a no-op placeholder if you want client-side filtering later

  return (
    <div data-testid="admin-seller-direct-payments">
      <p className="text-[#6b7a99] text-sm mb-4">Govern the seller direct-payments feature: global allow-list + per-seller activation</p>

      {/* Global toggle */}
      <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-4 mb-4 flex items-center gap-3 flex-wrap" data-testid="feature-toggle-card">
        <Wallet size={18} className="text-indigo-400" />
        <div className="flex-1 min-w-[160px]">
          <div className="text-white text-sm font-semibold">Direct Payments — Platform Feature</div>
          <div className="text-[11px] text-[#6b7a99]">When OFF, no seller can configure direct methods, and buyers don&apos;t see them.</div>
        </div>
        <Button size="sm" onClick={() => toggleFeature(!settings.feature_enabled)}
          className={`h-8 rounded-lg text-xs ${settings.feature_enabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-600 hover:bg-slate-700'}`}
          data-testid="toggle-feature-btn">
          {settings.feature_enabled ? 'ON' : 'OFF'}
        </Button>
      </div>

      {/* Method Types Registry — Add / Edit / Activate / Deactivate / Icon upload */}
      <PaymentMethodTypesManager onTypesChanged={fetchAll} />

      {/* Allow-list */}
      <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-4 mb-4" data-testid="allowlist-card">
        <h3 className="text-white text-sm font-semibold mb-1">Method Allow-list</h3>
        <p className="text-[11px] text-[#6b7a99] mb-3">Only the methods you tick here can be configured by sellers. Manage the full registry above to add custom types, upload icons, or activate/deactivate them.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {types.map(t => {
            const on = settings.allowed_types?.includes(t.id);
            return (
              <button key={t.id} onClick={() => toggleType(t.id)} data-testid={`allowtype-${t.id}`}
                className={`px-3 py-2 rounded-lg border text-xs text-left transition-colors ${on ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200' : 'border-[#252d3d] bg-[#0f1320] text-[#8896b0] hover:text-white'}`}>
                <div className="flex items-center gap-1.5">
                  {t.icon_url ? (
                    <img src={`${API_URL}${t.icon_url}`} alt="" className="w-3.5 h-3.5 object-contain" />
                  ) : (
                    on ? <Check size={12} weight="bold" /> : <XIcon size={12} />
                  )}
                  <span className="font-medium">{t.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Sellers */}
      <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-4" data-testid="sellers-list-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-white text-sm font-semibold">Per-Seller Activation</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6b7a99]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name/email"
                className="pl-8 h-8 w-48 text-sm bg-[#0f1320] border-[#252d3d] text-white" data-testid="seller-search-input" />
            </div>
            <Button size="sm" variant="outline" onClick={refreshSellers} className="border-[#252d3d] text-[#8896b0] hover:text-white h-8 rounded-lg text-xs">Search</Button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap mb-3 text-xs">
          <Button size="sm" onClick={() => bulkSet('enable', 'all')} className="bg-emerald-600 hover:bg-emerald-700 h-8 rounded-lg text-xs" data-testid="bulk-enable-all-btn">Enable for ALL sellers</Button>
          <Button size="sm" onClick={() => bulkSet('disable', 'all')} className="bg-red-600 hover:bg-red-700 h-8 rounded-lg text-xs" data-testid="bulk-disable-all-btn">Disable for ALL sellers</Button>
          {selected.size > 0 && (
            <>
              <Button size="sm" onClick={() => bulkSet('enable', 'selected')} className="bg-emerald-500 hover:bg-emerald-600 h-8 rounded-lg text-xs" data-testid="bulk-enable-selected-btn">Enable selected ({selected.size})</Button>
              <Button size="sm" onClick={() => bulkSet('disable', 'selected')} className="bg-red-500 hover:bg-red-600 h-8 rounded-lg text-xs" data-testid="bulk-disable-selected-btn">Disable selected ({selected.size})</Button>
            </>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto rounded-lg border border-[#252d3d]">
          <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 px-3 py-2 text-[10px] uppercase tracking-wider text-[#6b7a99] border-b border-[#252d3d]">
            <span></span><span>Seller</span><span>Country</span><span>Methods</span><span>Status</span>
          </div>
          {filtered.map(s => {
            const checked = selected.has(s.user_id);
            return (
              <div key={s.user_id} data-testid={`seller-row-${s.user_id}`}
                className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 px-3 py-2 items-center border-b border-[#252d3d]/40 hover:bg-[#1e2536]/40">
                <input type="checkbox" checked={checked} onChange={() => toggleSelect(s.user_id)} className="h-3.5 w-3.5 accent-indigo-500" data-testid={`select-seller-${s.user_id}`} />
                <div className="min-w-0">
                  <div className="text-white text-xs font-medium truncate">{s.name || '—'}</div>
                  <div className="text-[10px] text-[#6b7a99] truncate">{s.email}</div>
                </div>
                <span className="text-[11px] text-[#8896b0]">{s.country || '—'}</span>
                <span className="text-[11px] text-[#8896b0]">{s.methods_count}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${s.direct_payments_disabled ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                  {s.direct_payments_disabled ? 'Disabled' : 'Enabled'}
                </span>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="text-center text-[#6b7a99] py-6 text-sm">No sellers found.</div>}
        </div>
      </div>
    </div>
  );
};

export default SellerDirectPaymentsView;
